import styled, { css, keyframes } from "styled-components";
import { BRAND_C as C, COLORS } from "../../common/colors";

// ─── Tokens (imported from ./colors.js) ──────────────────────────────────────
// To change any color, edit BRAND_C/COLORS in colors.js — it cascades here.
//
// NOTE: the previous version of this file had several colors hardcoded as
// literal rgba() purple/gold values (background blobs, popular-badge glow,
// card shadows) left over from an earlier theme, even though BRAND_C itself
// is the green/gold Ayurvedic palette. This pass replaces those literals
// with COLORS.* / BRAND_C.* so the whole page is actually one consistent
// theme instead of green content on a purple-tinted backdrop.

const GOLD = COLORS.accentWarm; // #F9A825 — small premium accents only, per colors.js's own brand-hierarchy note

const SIDE_W      = "220px";   /* side panel width on desktop */
const SIDE_W_LG   = "260px";   /* wider on large screens */
const PANEL_RADIUS = "24px";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Page shell ───────────────────────────────────────────────────────────────
export const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(170deg, ${COLORS.bgWarm} 0%, ${COLORS.bgCool} 100%);
  padding-bottom: 88px;
`;

export const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// ─── Background decoration ────────────────────────────────────────────────────
export const BgBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
  &.b1 { width: 560px; height: 560px; background: ${COLORS.primaryGlow}; top: -140px; left: -160px; }
  &.b2 { width: 400px; height: 400px; background: rgba(249, 168, 37, 0.10); top: 40%; right: -120px; }
  &.b3 { width: 300px; height: 300px; background: ${COLORS.accentGlow}; bottom: 100px; left: 32%; }
`;

export const DotGrid = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background-image: radial-gradient(circle, rgba(46, 125, 50, 0.08) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(to bottom, transparent, rgba(0,0,0,0.45) 18%, rgba(0,0,0,0.45) 82%, transparent);
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────
// Layout/type restyled to match Rogyog.styles.js's compact hero (see
// HeroWrap/Eyebrow/Title/Desc there) — tighter type scale, no divider bar —
// but background left as the page's original green gradient (from Section).
export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 40px 24px 32px;
  animation: ${fadeUp} 0.65s ease both;

  @media (max-width: 600px) { padding: 28px 20px 24px; }
`;

export const HeroEyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 10px;
`;

export const HeroTitle = styled.h1`
  max-width: 640px;
  font-size: clamp(1.8rem, 4.2vw, 2.6rem);
  font-weight: 800;
  color: ${C.text};
  line-height: 1.15;
  margin: 0 auto 10px;
  letter-spacing: -0.02em;
`;

export const HeroDesc = styled.p`
  font-size: 0.95rem;
  color: ${C.textMid};
  line-height: 1.6;
  max-width: 520px;
  margin: 0 auto;
`;

// ─── Hero trust badges ────────────────────────────────────────────────────────
// Grid of white bordered cards (was borderless icon+text on the green hero) —
// matches Rogyog.styles.js's BadgeRow/BadgeCard/BadgeIconWrap shape.

export const TrustBadgeRow = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-width: 760px;
  margin: 22px auto 0;

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  background: #fff;
  text-align: left;
`;

export const TrustBadgeIconWrap = styled.span`
  flex: none;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: ${COLORS.dietPrimaryTint};
  color: ${C.brand};

  svg { width: 16px; height: 16px; }
`;

export const TrustBadgeTitle = styled.span`
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${C.text};
  line-height: 1.3;
`;

export const TrustBadgeSub = styled.span`
  display: block;
  font-size: 0.72rem;
  color: ${C.textSoft};
  line-height: 1.3;
`;

// ─── Section header ───────────────────────────────────────────────────────────
export const HeaderWrap = styled.div`
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  z-index: 1;
`;

export const Eyebrow = styled.p`
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.brand};
  margin-bottom: 10px;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.6rem, 3.5vw, 2.4rem);
  font-weight: 800;
  color: ${C.text};
  margin-bottom: 14px;
  letter-spacing: -0.02em;
`;

export const Divider = styled.div`
  width: 40px; height: 3px;
  background: linear-gradient(90deg, ${C.brand}, ${C.accent});
  border-radius: 2px;
  margin: 0 auto 16px;
`;

export const SectionDesc = styled.p`
  font-size: 1rem;
  color: ${C.textMid};
  line-height: 1.7;
  max-width: 560px;
  margin: 0 auto;
`;

// ─── Program tabs ─────────────────────────────────────────────────────────────
// Real programs from yogaPackage.json, not a fixed set of 3 — the tab list
// simply reflects however many entries the data has.

export const ProgramTabRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 40px;
  position: relative;
  z-index: 1;
`;

export const ProgramTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.15s;

  border: 1.5px solid ${({ $active }) => ($active ? "transparent" : C.border)};
  background: ${({ $active }) => ($active ? C.brand : C.surface)};
  color: ${({ $active }) => ($active ? "#ffffff" : C.textMid)};

  svg { width: 16px; height: 16px; flex: none; }

  &:hover { transform: translateY(-1px); }
  &:focus-visible { outline: 3px solid ${C.brandLight}; outline-offset: 2px; }

  @media (max-width: 600px) {
    padding: 9px 14px;
    font-size: 0.8rem;
  }
`;

// ─── Package block (the single active program's panel) ───────────────────────
export const PackageBlock = styled.div`
  position: relative;
  z-index: 1;
  animation: ${fadeIn} 0.4s ease both;
`;

// ─── Layout: left panel + right cards ─────────────────────────────────────────
export const PackageLayout = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  border-radius: ${PANEL_RADIUS};
  overflow: hidden;
  box-shadow: 0 4px 32px ${COLORS.primaryShadow}, 0 1px 4px rgba(0,0,0,0.04);

  @media (max-width: 700px) {
    flex-direction: column;
    border-radius: 20px;
  }
`;

// ─── Left: tall image side panel ─────────────────────────────────────────────
export const SidePanel = styled.div`
  position: relative;
  width: ${SIDE_W};
  flex-shrink: 0;
  min-height: 340px;
  overflow: hidden;

  @media (min-width: 1000px) { width: ${SIDE_W_LG}; }

  @media (max-width: 700px) {
    width: 100%;
    min-height: 180px;
    max-height: 200px;
  }
`;

export const SidePanelImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 25%;
  display: block;
  transition: transform 0.5s ease;

  ${SidePanel}:hover & {
    transform: scale(1.04);
  }
`;

export const SidePanelOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    ${COLORS.textDark}E6 0%,
    ${COLORS.textDark}80 30%,
    ${COLORS.textDark}00 58%
  );

  @media (max-width: 700px) {
    background: linear-gradient(
      to top,
      ${COLORS.textDark}D9 0%,
      ${COLORS.textDark}66 35%,
      ${COLORS.textDark}00 65%
    );
  }
`;

export const SidePanelMeta = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 22px 24px;

  @media (max-width: 700px) {
    padding: 14px 18px 18px;
  }
`;

export const PackageEyebrow = styled.p`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 6px;
`;

export const PackageName = styled.h3`
  font-size: clamp(1.1rem, 2.2vw, 1.5rem);
  font-weight: 800;
  color: #ffffff;
  white-space: pre-line;
  line-height: 1.22;
  margin-bottom: 12px;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
`;

export const PackageDivider = styled.div`
  width: 28px; height: 2px;
  background: linear-gradient(90deg, ${C.accent}, ${C.brandLight});
  border-radius: 2px;
`;

// ─── Right: cards area ────────────────────────────────────────────────────────
export const CardsArea = styled.div`
  flex: 1;
  background: ${C.surfaceAlt};
  border-left: 1.5px solid ${C.border};
  display: flex;
  align-items: center;
  padding: 28px 24px;

  @media (max-width: 700px) {
    border-left: none;
    border-top: 1.5px solid ${C.border};
    padding: 20px 16px;
  }
`;

export const CardsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(${({ $count }) => Math.min($count, 3)}, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

// ─── Price Card ───────────────────────────────────────────────────────────────
export const Card = styled.div`
  position: relative;
  background: ${({ $popular }) => ($popular ? C.brand : C.surface)};
  border: 1.5px solid ${({ $popular }) => ($popular ? "transparent" : C.border)};
  border-radius: 16px;
  padding: 26px 20px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  overflow: visible;

  box-shadow: ${({ $popular }) =>
    $popular
      ? `0 8px 32px ${COLORS.primaryGlow}, 0 2px 6px ${COLORS.primaryShadow}`
      : `0 2px 8px rgba(0,0,0,0.04)`};

  ${({ $popular }) => $popular && css`
    transform: translateY(-6px);
    @media (max-width: 700px) { transform: none; }
  `}

  &:hover {
    transform: ${({ $popular }) => ($popular ? "translateY(-10px)" : "translateY(-4px)")};
    box-shadow: ${({ $popular }) =>
      $popular
        ? `0 16px 44px ${COLORS.primaryGlow}`
        : `0 8px 22px ${COLORS.primaryShadow}`};
    @media (max-width: 700px) { transform: translateY(-2px); }
  }

  &:focus-visible {
    outline: 3px solid ${C.brandLight};
    outline-offset: 3px;
  }

  /* Cards go single-column on mobile (see CardsGrid), so the full
     desktop padding/spacing is no longer earning its keep — it just
     reads as empty space on a phone. Tighten everything here instead
     of relying on the desktop values. */
  @media (max-width: 700px) {
    padding: 18px 18px 16px;
    border-radius: 14px;
  }
`;

// ─── Popular badge ────────────────────────────────────────────────────────────
export const PopularBadge = styled.div`
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${GOLD};
  color: ${COLORS.textDark};
  font-size: 0.67rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 5px 13px;
  border-radius: 20px;
  box-shadow: 0 2px 10px rgba(249, 168, 37, 0.40);

  svg { width: 11px; height: 11px; }

  @media (max-width: 700px) {
    top: -10px;
    font-size: 0.62rem;
    padding: 4px 11px;
  }
`;

// ─── Plan chip ────────────────────────────────────────────────────────────────
export const PlanChip = styled.span`
  display: inline-block;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $popular }) => ($popular ? "rgba(255,255,255,0.78)" : C.brandLight)};
  background: ${({ $popular }) => ($popular ? "rgba(255,255,255,0.14)" : C.muted)};
  padding: 3px 11px;
  border-radius: 20px;
  margin-bottom: 14px;
  margin-top: 6px;

  @media (max-width: 700px) {
    margin-bottom: 10px;
    margin-top: 0;
  }
`;

// ─── Price ────────────────────────────────────────────────────────────────────
export const PriceBubble = styled.div`
  align-self: flex-start;
  margin-bottom: 12px;

  @media (max-width: 700px) {
    margin-bottom: 10px;
  }
`;

export const PriceBubbleInner = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  background: ${({ $popular }) => ($popular ? "rgba(255,255,255,0.14)" : C.surfaceAlt)};
  border: 1.5px solid ${({ $popular }) => ($popular ? "rgba(255,255,255,0.20)" : C.border)};
  border-radius: 12px;
  padding: 9px 14px;

  @media (max-width: 700px) {
    padding: 7px 12px;
  }
`;

export const PriceLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $popular }) => ($popular ? "rgba(255,255,255,0.60)" : C.textSoft)};
  margin-bottom: 2px;
`;

export const PriceValue = styled.span`
  font-size: 1.45rem;
  font-weight: 800;
  color: ${({ $popular }) => ($popular ? "#ffffff" : C.text)};
  letter-spacing: -0.02em;
  line-height: 1;

  @media (max-width: 700px) {
    font-size: 1.2rem;
  }
`;

// ─── Card title ───────────────────────────────────────────────────────────────
export const CardTitle = styled.h4`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ $popular }) => ($popular ? "#ffffff" : C.text)};
  margin-bottom: 14px;
  letter-spacing: -0.01em;
  line-height: 1.25;

  @media (max-width: 700px) {
    font-size: 0.98rem;
    margin-bottom: 10px;
  }
`;

// ─── Feature list ─────────────────────────────────────────────────────────────
export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  flex: 1;

  @media (max-width: 700px) {
    margin: 0 0 14px;
    gap: 7px;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.86rem;
  color: ${({ $popular }) => ($popular ? "rgba(255,255,255,0.82)" : C.textMid)};
  line-height: 1.5;

  svg {
    width: 13px;
    height: 13px;
    color: ${({ $popular }) => ($popular ? C.accent : C.brand)};
    margin-top: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 700px) {
    font-size: 0.84rem;
    line-height: 1.4;
  }
`;

// ─── Join / view-details button ───────────────────────────────────────────────
export const JoinBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 18px;
  border-radius: 12px;
  border: none;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  margin-top: auto;

  @media (max-width: 700px) {
    padding: 11px 16px;
    font-size: 0.85rem;
  }

  background: ${({ $popular }) => ($popular ? "#ffffff" : C.brand)};
  color:       ${({ $popular }) => ($popular ? C.brand   : "#ffffff")};
  box-shadow:  ${({ $popular }) =>
    $popular
      ? "0 4px 14px rgba(255,255,255,0.16)"
      : `0 4px 14px ${COLORS.primaryShadow}`};

  svg { width: 15px; height: 15px; transition: transform 0.2s; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ $popular }) =>
      $popular
        ? "0 6px 18px rgba(255,255,255,0.22)"
        : `0 6px 18px ${COLORS.primaryGlow}`};
    svg { transform: translateX(3px); }
  }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 3px solid ${C.accent}; outline-offset: 2px; }
`;

// ─── Consultation banner ──────────────────────────────────────────────────────

export const ConsultBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: ${C.surface};
  border: 1.5px solid ${C.border};
  border-radius: 16px;
  padding: 20px 26px;
  margin-top: 28px;
  position: relative;
  z-index: 1;

  @media (max-width: 700px) {
    padding: 18px 20px;
    justify-content: center;
    text-align: center;
  }
`;

export const ConsultInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 8px;
  }
`;

export const ConsultIconWrap = styled.span`
  flex: none;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS.dietPrimaryTint};
  color: ${C.brand};

  svg { width: 20px; height: 20px; }
`;

export const ConsultText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: ${C.text};

  strong { display: block; font-size: 0.96rem; margin-bottom: 2px; }
  span { color: ${C.textMid}; font-size: 0.85rem; }
`;

export const ConsultBtn = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: ${C.brand};
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 14px ${COLORS.primaryShadow};

  svg { width: 15px; height: 15px; transition: transform 0.2s; }

  &:hover { transform: translateY(-1px); svg { transform: translateX(3px); } }
  &:focus-visible { outline: 3px solid ${C.accent}; outline-offset: 2px; }
`;

// ─── "Why our programs work" grid ─────────────────────────────────────────────

export const WhySection = styled.div`
  margin-top: 80px;
  position: relative;
  z-index: 1;

  @media (max-width: 600px) { margin-top: 56px; }
`;

export const WhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const WhyCard = styled.div`
  text-align: center;
  background: ${C.surface};
  border: 1.5px solid ${C.border};
  border-radius: 16px;
  padding: 28px 20px;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${({ $index = 0 }) => $index * 0.08}s;
`;

export const WhyIconWrap = styled.div`
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS.dietPrimaryTint};
  color: ${C.brand};

  svg { width: 24px; height: 24px; }
`;

export const WhyTitle = styled.h3`
  font-size: 0.98rem;
  font-weight: 800;
  color: ${C.text};
  margin: 0 0 8px;
`;

export const WhyDesc = styled.p`
  font-size: 0.85rem;
  color: ${C.textMid};
  line-height: 1.55;
  margin: 0;
`;

// ─── CTA Banner ───────────────────────────────────────────────────────────────
export const CtaBanner = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${C.brandDark} 0%, ${C.brand} 60%, ${C.brandLight} 100%);
  border-radius: 24px;
  padding: 56px 40px;
  text-align: center;
  margin-top: 72px;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 40px 24px;
    border-radius: 18px;
    margin-top: 48px;
  }
`;

export const BannerOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  &.o1 { width: 300px; height: 300px; background: rgba(255,255,255,0.06); top: -80px; left: -80px; }
  &.o2 { width: 200px; height: 200px; background: rgba(249, 168, 37, 0.14); bottom: -60px; right: -40px; }
`;

export const BannerTitle = styled.h2`
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 14px;
  position: relative;
  letter-spacing: -0.02em;
`;

export const BannerDesc = styled.p`
  font-size: 1rem;
  color: rgba(255,255,255,0.78);
  line-height: 1.7;
  max-width: 500px;
  margin: 0 auto 28px;
  position: relative;
`;

export const BannerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: #ffffff;
  color: ${C.brand};
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 20px rgba(0,0,0,0.13);
  letter-spacing: 0.01em;

  svg { width: 16px; height: 16px; transition: transform 0.2s; }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,0,0,0.18);
    svg { transform: translateX(3px); }
  }
  &:active { transform: translateY(0); }
  &:focus-visible { outline: 3px solid ${C.accent}; outline-offset: 3px; }
`;